import React, { Component, ErrorInfo, ReactNode } from 'react';

interface ErrorBoundaryProps {
  children: ReactNode;
  fallback?: (error: Error, reset: () => void) => ReactNode;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error?: Error;
  errorInfo?: ErrorInfo;
}

export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  public state: ErrorBoundaryState = {
    hasError: false,
  };

  public static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error };
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    this.setState({ errorInfo });
    
    // Log to Faro if available
    if (typeof window !== 'undefined' && (window as any).faro) {
      (window as any).faro.api.pushError(error, {
        context: {
          componentStack: errorInfo.componentStack,
        },
      });
    }
    
    console.error('[ErrorBoundary] Error:', error);
    console.error('[ErrorBoundary] Component Stack:', errorInfo.componentStack);
  }

  private handleReset = (): void => {
    this.setState({ hasError: false, error: undefined, errorInfo: undefined });
    window.location.reload();
  };

  public render(): ReactNode {
    if (this.state.hasError && this.state.error) {
      if (this.props.fallback) {
        return this.props.fallback(this.state.error, this.handleReset);
      }

      return (
        <div style={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          padding: '2rem',
        }}>
          <div style={{
            maxWidth: '600px',
            width: '100%',
            background: 'white',
            borderRadius: '16px',
            boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
            padding: '3rem',
            textAlign: 'center',
          }}>
            <div style={{
              width: '80px',
              height: '80px',
              margin: '0 auto 2rem',
              background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '3rem',
            }}>
              😵
            </div>
            
            <h1 style={{
              fontSize: '2rem',
              fontWeight: 'bold',
              color: '#1f2937',
              marginBottom: '1rem',
            }}>
              Oops! Something went wrong
            </h1>
            
            <p style={{
              color: '#6b7280',
              marginBottom: '2rem',
              fontSize: '1.1rem',
              lineHeight: '1.6',
            }}>
              We've encountered an unexpected error. Don't worry, our team has been notified and we're working on it.
            </p>

            <details style={{
              textAlign: 'left',
              background: '#f9fafb',
              padding: '1rem',
              borderRadius: '8px',
              marginBottom: '2rem',
              cursor: 'pointer',
            }}>
              <summary style={{
                fontWeight: '600',
                color: '#374151',
                marginBottom: '0.5rem',
              }}>
                Technical Details
              </summary>
              <pre style={{
                fontSize: '0.875rem',
                color: '#dc2626',
                overflow: 'auto',
                marginTop: '0.5rem',
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word',
              }}>
                {this.state.error.message}
              </pre>
            </details>

            <button
              onClick={this.handleReset}
              style={{
                padding: '0.75rem 2rem',
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                fontSize: '1rem',
                fontWeight: '600',
                cursor: 'pointer',
                transition: 'transform 0.2s',
              }}
              onMouseOver={(e) => e.currentTarget.style.transform = 'scale(1.05)'}
              onMouseOut={(e) => e.currentTarget.style.transform = 'scale(1)'}
            >
              Reload Page
            </button>

            <p style={{
              marginTop: '2rem',
              fontSize: '0.875rem',
              color: '#9ca3af',
            }}>
              If this problem persists, please contact support
            </p>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}
